import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { supabaseClient } from '../services/supabase';
import { UserProfile, UserRole } from '../types/user';

interface UserRoleContextType {
  userProfile: UserProfile | null;
  loading: boolean;
  refreshProfile: () => Promise<void>;
  hasRole: (role: UserRole) => boolean;
  hasAnyRole: (roles: UserRole[]) => boolean;
}

const UserRoleContext = createContext<UserRoleContextType | undefined>(undefined);

export const useUserRole = () => {
  const context = useContext(UserRoleContext);
  if (context === undefined) {
    throw new Error('useUserRole must be used within a UserRoleProvider');
  }
  return context;
};

interface UserRoleProviderProps {
  children: React.ReactNode;
  userId: string | null | undefined;
}

export const UserRoleProvider: React.FC<UserRoleProviderProps> = ({ children, userId }) => {
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchUserProfile = useCallback(async () => {
    if (!userId) {
      setLoading(false);
      setUserProfile(null);
      return;
    }

    try {
      const { data, error } = await supabaseClient
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) {
        console.error('Error fetching user profile:', error);
        // Create default profile if doesn't exist
        const defaultProfile: UserProfile = {
          id: userId,
          email: '',
          role: 'developer',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
        setUserProfile(defaultProfile);
      } else {
        setUserProfile(data);
      }
    } catch (error) {
      console.error('Error in fetchUserProfile:', error);
      // Create default profile on error
      const defaultProfile: UserProfile = {
        id: userId,
        email: '',
        role: 'developer',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      setUserProfile(defaultProfile);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  const refreshProfile = async () => {
    setLoading(true);
    await fetchUserProfile();
  };

  const hasRole = (role: UserRole): boolean => {
    return userProfile?.role === role;
  };

  const hasAnyRole = (roles: UserRole[]): boolean => {
    return userProfile ? roles.includes(userProfile.role) : false;
  };

  useEffect(() => {
    if (userId) {
      fetchUserProfile();
    } else {
      // No user ID, set loading to false and userProfile to null
      setLoading(false);
      setUserProfile(null);
    }
  }, [userId, fetchUserProfile]);

  const value: UserRoleContextType = {
    userProfile,
    loading,
    refreshProfile,
    hasRole,
    hasAnyRole
  };

  return (
    <UserRoleContext.Provider value={value}>
      {children}
    </UserRoleContext.Provider>
  );
};
